import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Activity, Search, Clock, MapPin, Monitor, Printer, Download, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
const roleColor: Record<string, string> = {
  Administrator: 'bg-red-100 text-red-700',
  Reception: 'bg-blue-100 text-blue-700',
  Nursing: 'bg-green-100 text-green-700',
  Clinician: 'bg-indigo-100 text-indigo-700',
  Laboratory: 'bg-yellow-100 text-yellow-700',
  Radiology: 'bg-purple-100 text-purple-700',
  Pharmacy: 'bg-pink-100 text-pink-700'
};
const formatDuration = (loginAt: string, logoutAt?: string) => {
  const start = new Date(loginAt).getTime();
  const end = logoutAt ? new Date(logoutAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};
export const ActivityLog: React.FC = () => {
  const {
    loginSessions,
    deleteLoginSessions,
    deleteLoginSessionsBefore,
    deleteLoginSessionsInRange
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Ended'>('All');
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearMode, setClearMode] = useState<'before' | 'range' | 'all'>('before');
  const [clearStart, setClearStart] = useState('');
  const [clearEnd, setClearEnd] = useState('');
  const sortedSessions = useMemo(() => {
    return [...loginSessions].sort((a, b) => new Date(b.loginAt).getTime() - new Date(a.loginAt).getTime());
  }, [loginSessions]);
  const filtered = useMemo(() => {
    return sortedSessions.filter(s => {
      const matchesSearch = s.userName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === 'All' || s.role === roleFilter;
      const matchesStatus = statusFilter === 'All' || statusFilter === 'Active' && !s.logoutAt || statusFilter === 'Ended' && !!s.logoutAt;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [sortedSessions, searchQuery, roleFilter, statusFilter]);
  const activeCount = sortedSessions.filter(s => !s.logoutAt).length;
  const todayCount = sortedSessions.filter(s => new Date(s.loginAt).toDateString() === new Date().toDateString()).length;
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPDF = () => {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });
    const margin = 30;
    let y = margin;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('User Activity Log', margin, y);
    y += 18;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}  ·  ${filtered.length} sessions`, margin, y);
    y += 18;
    // Column headers
    const cols = [{
      label: 'User',
      x: margin,
      w: 110
    }, {
      label: 'Role',
      x: margin + 110,
      w: 70
    }, {
      label: 'Login',
      x: margin + 180,
      w: 110
    }, {
      label: 'Logout',
      x: margin + 290,
      w: 110
    }, {
      label: 'Duration',
      x: margin + 400,
      w: 60
    }, {
      label: 'Location',
      x: margin + 460,
      w: 140
    }, {
      label: 'Device',
      x: margin + 600,
      w: 180
    }];
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    cols.forEach(c => pdf.text(c.label, c.x, y));
    y += 4;
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, margin + 780, y);
    y += 10;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const pageHeight = pdf.internal.pageSize.getHeight() - margin;
    filtered.forEach(s => {
      if (y > pageHeight) {
        pdf.addPage();
        y = margin;
      }
      const row = [s.userName, s.role, new Date(s.loginAt).toLocaleString(), s.logoutAt ? new Date(s.logoutAt).toLocaleString() : '—', formatDuration(s.loginAt, s.logoutAt), s.location || '—', s.device || '—'];
      cols.forEach((c, i) => {
        const text = String(row[i] || '—');
        const lines = pdf.splitTextToSize(text, c.w - 5);
        pdf.text(lines, c.x, y);
      });
      y += 16;
    });
    pdf.save(`activity-log-${new Date().toISOString().slice(0, 10)}.pdf`);
  };
  const openClearModal = () => {
    setClearMode('before');
    setClearStart('');
    setClearEnd('');
    setShowClearModal(true);
  };
  const handleConfirmClear = () => {
    if (clearMode === 'before' && clearStart) {
      const cutoffISO = new Date(clearStart).toISOString();
      const affected = sortedSessions.filter(s => new Date(s.loginAt).getTime() < new Date(cutoffISO).getTime()).length;
      if (affected > 0 && confirm(`Delete ${affected} session(s) logged before ${new Date(cutoffISO).toLocaleDateString()}?`)) {
        deleteLoginSessionsBefore(cutoffISO);
      }
    } else if (clearMode === 'range' && clearStart && clearEnd) {
      const startISO = new Date(clearStart).toISOString();
      const endISO = new Date(clearEnd + 'T23:59:59').toISOString();
      const affected = sortedSessions.filter(s => {
        const t = new Date(s.loginAt).getTime();
        return t >= new Date(startISO).getTime() && t <= new Date(endISO).getTime();
      }).length;
      if (affected > 0 && confirm(`Delete ${affected} session(s) logged between ${new Date(startISO).toLocaleDateString()} and ${new Date(endISO).toLocaleDateString()}?`)) {
        deleteLoginSessionsInRange(startISO, endISO);
      }
    } else if (clearMode === 'all') {
      if (confirm(`Permanently delete ALL ${sortedSessions.length} session record(s)? This cannot be undone.`)) {
        deleteLoginSessions(sortedSessions.map(s => s.id));
      }
    }
    setShowClearModal(false);
  };
  return <div className="space-y-6" data-id="element-105">
      <div className="flex justify-between items-start gap-4 flex-wrap print:hidden" data-id="element-106">
        <div data-id="element-107">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-108">
            User Activity Log
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-109">
            Login and logout history for all personnel
          </p>
        </div>
        <div className="flex gap-2 flex-wrap" data-id="element-110">
          <Button variant="outline" onClick={handlePrint} data-id="element-111">
            <Printer className="w-4 h-4 mr-2" data-id="element-112" /> Print
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} data-id="element-113">
            <Download className="w-4 h-4 mr-2" data-id="element-114" /> Download PDF
          </Button>
          <Button variant="outline" onClick={openClearModal} className="text-red-600 hover:bg-red-50" data-id="element-115">
            <Trash2 className="w-4 h-4 mr-2" data-id="element-116" /> Clear Log
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-117">
        <Card data-id="element-118">
          <div className="flex items-center" data-id="element-119">
            <div className="bg-green-100 p-3 rounded-full" data-id="element-120">
              <Activity className="w-6 h-6 text-green-600" data-id="element-121" />
            </div>
            <div className="ml-4" data-id="element-122">
              <p className="text-sm text-gray-500" data-id="element-123">Currently Online</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-124">{activeCount}</p>
            </div>
          </div>
        </Card>
        <Card data-id="element-125">
          <div className="flex items-center" data-id="element-126">
            <div className="bg-blue-100 p-3 rounded-full" data-id="element-127">
              <Clock className="w-6 h-6 text-blue-600" data-id="element-128" />
            </div>
            <div className="ml-4" data-id="element-129">
              <p className="text-sm text-gray-500" data-id="element-130">Logins Today</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-131">{todayCount}</p>
            </div>
          </div>
        </Card>
        <Card data-id="element-132">
          <div className="flex items-center" data-id="element-133">
            <div className="bg-purple-100 p-3 rounded-full" data-id="element-134">
              <Activity className="w-6 h-6 text-purple-600" data-id="element-135" />
            </div>
            <div className="ml-4" data-id="element-136">
              <p className="text-sm text-gray-500" data-id="element-137">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-138">
                {sortedSessions.length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card data-id="element-139">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-140">
          <div className="relative" data-id="element-141">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-142">
              <Search className="h-5 w-5 text-gray-400" data-id="element-143" />
            </div>
            <input type="text" placeholder="Search by user name..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-144" />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-145">
            <option value="All" data-id="element-146">All Roles</option>
            <option value="Administrator" data-id="element-147">Administrator</option>
            <option value="Reception" data-id="element-148">Reception</option>
            <option value="Nursing" data-id="element-149">Nursing</option>
            <option value="Clinician" data-id="element-150">Clinician</option>
            <option value="Laboratory" data-id="element-151">Laboratory</option>
            <option value="Radiology" data-id="element-152">Radiology</option>
            <option value="Pharmacy" data-id="element-153">Pharmacy</option>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-154">
            <option value="All" data-id="element-155">All Statuses</option>
            <option value="Active" data-id="element-156">Currently Online</option>
            <option value="Ended" data-id="element-157">Logged Out</option>
          </select>
        </div>
      </Card>

      {/* Sessions table */}
      <Card noPadding data-id="element-158">
        <div className="overflow-x-auto" data-id="element-159">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-160">
            <thead className="bg-gray-50" data-id="element-161">
              <tr data-id="element-162">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-163">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-164">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-165">
                  Login Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-166">
                  Logout Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-167">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-168">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-169">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-170">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-171">
              {filtered.length > 0 ? filtered.map(s => <tr key={s.id} className="hover:bg-gray-50" data-id="element-172">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-173">
                      {s.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-174">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor[s.role] || 'bg-gray-100 text-gray-700'}`} data-id="element-175">
                        {s.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-176">
                      {new Date(s.loginAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-177">
                      {s.logoutAt ? new Date(s.logoutAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-178">
                      {formatDuration(s.loginAt, s.logoutAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700" data-id="element-179">
                      <div className="flex items-start gap-1.5 max-w-[14rem]" data-id="element-180">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-181" />
                        <div data-id="element-182">
                          <p className="text-sm text-gray-800" data-id="element-183">
                            {s.location || '—'}
                          </p>
                          {s.ipAddress && <p className="text-xs text-gray-400 font-mono" data-id="element-184">
                              {s.ipAddress}
                            </p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700" data-id="element-185">
                      <div className="flex items-start gap-1.5 max-w-[14rem]" data-id="element-186">
                        <Monitor className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-187" />
                        <span className="text-sm text-gray-800" data-id="element-188">
                          {s.device || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-189">
                      {s.logoutAt ? <Badge variant="default" data-id="element-190">Ended</Badge> : <Badge variant="success" data-id="element-191">
                          <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full mr-1 align-middle animate-pulse" data-id="element-192"></span>
                          Online
                        </Badge>}
                    </td>
                  </tr>) : <tr data-id="element-193">
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-194">
                    No login sessions match your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Clear log modal */}
      {showClearModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 print:hidden" data-id="element-195">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-196">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-197">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-198">
                Clear Activity Log
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-199">
                Permanently delete session records. This cannot be undone.
              </p>
            </div>
            <div className="p-6 space-y-4" data-id="element-200">
              <div className="space-y-2" data-id="element-201">
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-id="element-202">
                  <input type="radio" name="clearMode" checked={clearMode === 'before'} onChange={() => setClearMode('before')} className="mt-1" data-id="element-203" />
                  <span data-id="element-204">
                    <span className="font-medium" data-id="element-205">
                      Delete sessions logged BEFORE a date
                    </span>
                    <span className="block text-xs text-gray-500" data-id="element-206">
                      Useful for clearing out old history while keeping recent
                      activity.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-id="element-207">
                  <input type="radio" name="clearMode" checked={clearMode === 'range'} onChange={() => setClearMode('range')} className="mt-1" data-id="element-208" />
                  <span data-id="element-209">
                    <span className="font-medium" data-id="element-210">
                      Delete sessions within a date range
                    </span>
                    <span className="block text-xs text-gray-500" data-id="element-211">
                      Removes everything logged between two specific dates
                      (inclusive).
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-id="element-212">
                  <input type="radio" name="clearMode" checked={clearMode === 'all'} onChange={() => setClearMode('all')} className="mt-1" data-id="element-213" />
                  <span data-id="element-214">
                    <span className="font-medium text-red-600" data-id="element-215">
                      Delete ALL session records
                    </span>
                    <span className="block text-xs text-gray-500" data-id="element-216">
                      Wipes the entire activity log.
                    </span>
                  </span>
                </label>
              </div>

              {clearMode === 'before' && <div data-id="element-217">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-218">
                    Cutoff date
                  </label>
                  <input type="date" value={clearStart} onChange={e => setClearStart(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-219" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-220">
                    All sessions logged before this date will be deleted.
                  </p>
                </div>}

              {clearMode === 'range' && <div className="grid grid-cols-2 gap-3" data-id="element-221">
                  <div data-id="element-222">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-223">
                      Start date
                    </label>
                    <input type="date" value={clearStart} onChange={e => setClearStart(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-224" />
                  </div>
                  <div data-id="element-225">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-226">
                      End date
                    </label>
                    <input type="date" value={clearEnd} onChange={e => setClearEnd(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-227" />
                  </div>
                </div>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50" data-id="element-228">
              <Button variant="outline" onClick={() => setShowClearModal(false)} data-id="element-229">
                Cancel
              </Button>
              <Button onClick={handleConfirmClear} className="bg-red-600 hover:bg-red-700 text-white" disabled={clearMode === 'before' && !clearStart || clearMode === 'range' && (!clearStart || !clearEnd)} data-id="element-230">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-231" /> Delete Sessions
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};